/**
 * WhatsAppTemplatesPage
 *
 * Inventory of WhatsApp Business templates for the brand. Each card shows
 * the template's name, category, language, approval status from Meta,
 * variable count, and a short preview of the body.
 *
 * Actions per template:
 *   - Edit         (only while pending/rejected; approved templates are locked)
 *   - Duplicate    (creates a pending copy you can freely edit)
 *   - Submit       (re-submit a pending/rejected template to Meta)
 *   - Delete       (only if no campaign uses it)
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Sparkles,
  Search,
  Filter,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  Send,
  CheckCircle2,
  Clock,
  XCircle,
  FileText,
  MessageCircle,
  Loader2,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { ConfirmDangerModal } from "../../../shared/components/ui/ConfirmDangerModal";
import { toast } from "../../../shared/hooks/useToast";
import WhatsAppSubNav from "../components/WhatsAppSubNav";
import { whatsappAccountsApi } from "../api";
import { whatsappApi } from "../api";
import type {
  TemplateApprovalStatus,
  WhatsAppTemplate,
} from "../types";
import { AITemplateGenerator } from "../components/AITemplateGenerator";

const MOCK_BRAND_ID = "brand-demo";

const STATUS_META: Record<
  TemplateApprovalStatus,
  { label: string; classes: string; icon: typeof CheckCircle2 }
> = {
  approved: {
    label: "Aprobado",
    classes: "bg-emerald-50 text-emerald-700 border-emerald-100",
    icon: CheckCircle2,
  },
  pending: {
    label: "Pendiente",
    classes: "bg-amber-50 text-amber-700 border-amber-100",
    icon: Clock,
  },
  rejected: {
    label: "Rechazado",
    classes: "bg-red-50 text-red-700 border-red-100",
    icon: XCircle,
  },
};

export function WhatsAppTemplatesPage() {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  /* ─── Load templates ─── */
  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await whatsappApi.getTemplates(MOCK_BRAND_ID);
      if (res.success) setTemplates(res.data);
      else setError(res.error || "Error al cargar plantillas");
    } catch (e: any) {
      setError(e.message || "Error inesperado");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  /* Accounts count para el subnav */
  const [accountsCount, setAccountsCount] = useState<number | undefined>(undefined);
  useEffect(() => {
    whatsappAccountsApi.list().then((r) => {
      if (r.success) setAccountsCount(r.data.length);
    });
  }, []);

  /* ─── Filters (solo búsqueda — sin status Meta) ─── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (q) {
        const hay = `${t.name} ${t.body}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [templates, search]);

  /* ─── Stats ─── */
  const stats = useMemo(() => {
    return {
      total: templates.length,
      generatedByAI: templates.filter((t) => t.generatedByAI).length,
      withMedia: templates.filter(
        (t) => t.header && t.header.type !== "none" && t.header.type !== "text",
      ).length,
      withButtons: templates.filter((t) => (t.buttons?.length ?? 0) > 0).length,
    };
  }, [templates]);

  /* ─── Actions ─── */
  const handleDuplicate = async (id: string) => {
    setActionBusy(id);
    setOpenMenuId(null);
    const res = await whatsappApi.duplicateTemplate(id);
    if (res.success) {
        await loadTemplates();
        toast.success("Plantilla duplicada — podés editarla libremente");
    } else {
        const msg = res.error || "Error al duplicar";
        setError(msg);
        toast.error(msg);
    }
    setActionBusy(null);
  };

  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  const handleDelete = (id: string, name: string) => {
    setOpenMenuId(null);
    setPendingDelete({ id, name });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setActionBusy(pendingDelete.id);
    const res = await whatsappApi.deleteTemplate(pendingDelete.id);
    if (res.success) {
        await loadTemplates();
        toast.success("Plantilla eliminada");
    } else {
        const msg = res.error || "Error al borrar";
        setError(msg);
        toast.error(msg);
    }
    setActionBusy(null);
    setPendingDelete(null);
  };

  // submitTemplateToMeta queda removido — al pasar a QR no hay flow Meta.

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-7xl space-y-6">
        <WhatsAppSubNav active="messages" accountsCount={accountsCount} />

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                Mensajes guardados
              </h1>
              <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold">
                {templates.length}
              </span>
            </div>
            <p className="text-slate-500 text-sm mt-1">
              Mensajes reusables con variables — los podés mandar desde cualquier cuenta vinculada.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowAIGenerator(true)}
            >
              <Sparkles size={16} />
              Crear con IA
            </Button>
            <Link to="/whatsapp/templates/new">
              <Button variant="primary">
                <Plus size={16} />
                Nueva plantilla
              </Button>
            </Link>
          </div>
        </div>

        {/* KPI strip — simplificado a 3 stats relevantes para "mensajes guardados" */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            label="Total"
            value={stats.total}
            icon={FileText}
            iconColor="text-slate-500"
            iconBg="bg-slate-100"
          />
          <StatCard
            label="Creados por IA"
            value={stats.generatedByAI}
            icon={Sparkles}
            iconColor="text-violet-600"
            iconBg="bg-violet-100"
          />
          <StatCard
            label="Con botones"
            value={stats.withButtons}
            icon={MessageCircle}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-100"
          />
        </div>

        {/* Search (sin filtro de status Meta) */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <div className="relative">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o contenido..."
              className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm
                focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
            <XCircle size={18} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        {/* Grid / empty / loading */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-100 shadow-sm">
            <Loader2 className="animate-spin text-emerald-500 mb-4" size={36} />
            <p className="text-slate-500">Cargando plantillas...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-100 shadow-sm">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
              <FileText size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-700 font-medium mb-2">
              {templates.length === 0
                ? "Todavía no tenés plantillas"
                : "Ninguna coincide con tu búsqueda"}
            </p>
            <p className="text-slate-500 text-sm mb-5">
              {templates.length === 0
                ? "Creá tu primera plantilla manualmente o pedile a la IA que te sugiera una."
                : "Probá con otras palabras."}
            </p>
            {templates.length === 0 && (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setShowAIGenerator(true)}
                >
                  <Sparkles size={15} />
                  Crear con IA
                </Button>
                <Link to="/whatsapp/templates/new">
                  <Button variant="primary">
                    <Plus size={15} />
                    Nueva plantilla
                  </Button>
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                busy={actionBusy === t.id}
                menuOpen={openMenuId === t.id}
                onMenuToggle={() =>
                  setOpenMenuId(openMenuId === t.id ? null : t.id)
                }
                onMenuClose={() => setOpenMenuId(null)}
                onEdit={() => navigate(`/whatsapp/templates/${t.id}`)}
                onDuplicate={() => handleDuplicate(t.id)}
                onDelete={() => handleDelete(t.id, t.name)}
                onUse={() =>
                  navigate(`/whatsapp?template=${encodeURIComponent(t.id)}`)
                }
              />
            ))}
          </div>
        )}
      </div>

      <AITemplateGenerator
        open={showAIGenerator}
        onClose={() => setShowAIGenerator(false)}
        onAccept={async (tpl) => {
          // Persist via API too (fire-and-forget — if backend is down we still
          // get the optimistic UI update)
          await whatsappApi
            .createTemplate({
              brandId: MOCK_BRAND_ID,
              name: tpl.name,
              category: tpl.category,
              language: tpl.language,
              header: tpl.header,
              body: tpl.body,
              footer: tpl.footer,
              buttons: tpl.buttons,
            })
            .catch(() => null);
          await loadTemplates();
        }}
      />

      {pendingDelete && (
        <ConfirmDangerModal
          title="Borrar plantilla"
          description={`¿Borrar la plantilla "${pendingDelete.name}"? Esta acción no se puede deshacer.`}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
          busy={actionBusy === pendingDelete.id}
        />
      )}
    </div>
  );
}

/* ─── Sub-components ─── */

interface StatCardProps {
  label: string;
  value: number;
  icon: typeof FileText;
  iconColor: string;
  iconBg: string;
}

function StatCard({ label, value, icon: Icon, iconColor, iconBg }: StatCardProps) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-center gap-3">
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}
      >
        <Icon size={18} className={iconColor} />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 tracking-tight leading-none">
          {value}
        </p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FilterChip({ label, active, onClick }: FilterChipProps) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
        active
          ? "bg-emerald-600 text-white"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {label}
    </button>
  );
}

interface TemplateCardProps {
  template: WhatsAppTemplate;
  busy: boolean;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onUse: () => void;
}

function TemplateCard({
  template,
  busy,
  menuOpen,
  onMenuToggle,
  onMenuClose,
  onEdit,
  onDuplicate,
  onDelete,
  onUse,
}: TemplateCardProps) {
  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm p-5 transition-all hover:shadow-md ${
        busy
          ? "opacity-60 pointer-events-none border-slate-100"
          : "border-slate-100 hover:border-slate-200"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            {template.generatedByAI && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-violet-50 text-violet-700 border-violet-100 flex items-center gap-1">
                <Sparkles size={9} />
                IA
              </span>
            )}
            {(template.buttons?.length ?? 0) > 0 && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                {template.buttons?.length === 1
                  ? "1 botón"
                  : `${template.buttons?.length} botones`}
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-slate-900 truncate capitalize">
            {template.name.replace(/_/g, " ")}
          </h3>
          <p className="text-[10px] text-slate-400 mt-0.5">
            {template.variableCount}{" "}
            {template.variableCount === 1 ? "variable" : "variables"}
          </p>
        </div>

        {/* Kebab menu */}
        <div className="relative flex-shrink-0">
          <button
            onClick={onMenuToggle}
            aria-label="Acciones del mensaje"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            {busy ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <MoreVertical size={15} />
            )}
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={onMenuClose} />
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border border-slate-200 shadow-lg z-20 overflow-hidden w-44 animate-fade-in">
                <MenuItem icon={Pencil} label="Editar" onClick={onEdit} />
                <MenuItem icon={Copy} label="Duplicar" onClick={onDuplicate} />
                <div className="border-t border-slate-100" />
                <MenuItem
                  icon={Trash2}
                  label="Borrar"
                  onClick={onDelete}
                  danger
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Body preview */}
      <div className="bg-slate-50 rounded-xl p-3 mb-3 max-h-28 overflow-hidden relative">
        <p className="text-xs text-slate-700 whitespace-pre-wrap line-clamp-4 leading-relaxed">
          {template.body}
        </p>
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none" />
      </div>

      {/* Footer actions: cualquier mensaje guardado se puede usar */}
      <div className="flex items-center gap-2">
        <button
          onClick={onUse}
          className="flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all bg-emerald-600 text-white hover:bg-emerald-700"
          title="Usar en una campaña nueva"
        >
          <MessageCircle size={12} />
          Usar
        </button>
        <button
          onClick={onEdit}
          className="px-3 py-2 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all flex items-center gap-1.5"
        >
          <Pencil size={12} />
          Editar
        </button>
      </div>

      {/* Footer secundario: fecha */}
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-end text-[10px] text-slate-400">
        <span>
          Creado{" "}
          {new Date(template.createdAt || Date.now()).toLocaleDateString(
            "es-AR",
            { day: "numeric", month: "short" },
          )}
        </span>
      </div>
    </div>
  );
}

interface MenuItemProps {
  icon: typeof Pencil;
  label: string;
  onClick: () => void;
  danger?: boolean;
}

function MenuItem({ icon: Icon, label, onClick, danger }: MenuItemProps) {
  return (
    <button
      onClick={onClick}
      className={`w-full px-3 py-2 text-left flex items-center gap-2 text-xs transition-colors ${
        danger
          ? "text-red-600 hover:bg-red-50"
          : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      <Icon size={13} />
      {label}
    </button>
  );
}

export default WhatsAppTemplatesPage;
